import { InboxView } from '@weave/schema-gen-ts/dist/schemas/sms/data/v4/sms_service.pb';
import { Bool } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { ThreadAIOutcome } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { Thread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { isSmsMessageItem } from '../../../utils/thread-items-utils';
import { ListUnifiedThreadsIO } from '../../types';
import { convertUnifiedThreadToApplicableThreadReadStatuses } from './convert-unified-thread-to-applicable-thread-read-statuses';

type ThreadCouldBeInListUnifiedThreadsResponseArgs = {
  thread: Thread;
  listUnifiedThreadsRequest: ListUnifiedThreadsIO['input'];
};

export const threadCouldBeInListUnifiedThreadsResponse = ({
  thread,
  listUnifiedThreadsRequest,
}: ThreadCouldBeInListUnifiedThreadsResponseArgs): boolean => {
  // locationIds
  if (
    listUnifiedThreadsRequest.locationIds.length &&
    !listUnifiedThreadsRequest.locationIds.includes(thread.locationId)
  ) {
    return false;
  }

  // unifiedFilter.personPhones - Check first SMS item in items array
  if (listUnifiedThreadsRequest.unifiedFilter?.personPhones?.length && thread.items?.length) {
    const firstSmsItem = thread.items.find(isSmsMessageItem);

    if (!firstSmsItem?.smsMessage?.personPhone) {
      return false;
    }

    const matchesPersonPhone = listUnifiedThreadsRequest.unifiedFilter.personPhones.some(
      (personPhone) => formatPhoneNumberE164(personPhone) === formatPhoneNumberE164(firstSmsItem.smsMessage.personPhone)
    );

    if (!matchesPersonPhone) {
      return false;
    }
  }

  // unifiedFilter.locationPhones - Check thread.locationPhone or first SMS item's locationPhone
  if (listUnifiedThreadsRequest.unifiedFilter?.locationPhones?.length) {
    const firstSmsItem = thread.items?.find(isSmsMessageItem);
    const threadLocationPhone = thread.locationPhone || firstSmsItem?.smsMessage?.locationPhone || '';

    const matchesLocationPhone = listUnifiedThreadsRequest.unifiedFilter.locationPhones.some(
      (locationPhone) => formatPhoneNumberE164(locationPhone) === formatPhoneNumberE164(threadLocationPhone)
    );

    if (!matchesLocationPhone) {
      return false;
    }
  }

  // unifiedFilter.statuses
  const statusFilters = listUnifiedThreadsRequest.unifiedFilter?.statuses ?? [];
  const hasStatusFilters = !!statusFilters.length;
  const applicableThreadReadStatuses = convertUnifiedThreadToApplicableThreadReadStatuses(thread);
  if (
    hasStatusFilters &&
    applicableThreadReadStatuses?.length &&
    !applicableThreadReadStatuses.some((threadReadStatus) =>
      listUnifiedThreadsRequest.unifiedFilter?.statuses?.includes(threadReadStatus)
    )
  ) {
    return false;
  }

  // unifiedFilter.inboxView
  const inboxView = listUnifiedThreadsRequest.unifiedFilter?.inboxView ?? InboxView.UNSPECIFIED;
  if (inboxView === InboxView.ACTIONABLE && thread.actionable === false) {
    return false;
  } else if (inboxView === InboxView.BLOCKED && thread.isBlocked === false) {
    return false;
  } else if (inboxView !== InboxView.BLOCKED && thread.isBlocked === true) {
    return false;
  } else if (inboxView === InboxView.ARCHIVED && !thread.isArchived) {
    return false;
  } else if (inboxView !== InboxView.ARCHIVED && thread.isArchived) {
    return false;
  }

  // unifiedFilter.excludeAutomatedLastMessage is not supported for thread updates. SMS updaters should be used instead.

  // unifiedFilter.personIds
  if (
    listUnifiedThreadsRequest.unifiedFilter?.personIds?.length &&
    !listUnifiedThreadsRequest.unifiedFilter.personIds.includes(thread.person?.personId ?? 'invalid-id') // Always false if personId isn't present
  ) {
    return false;
  }

  // unifiedFilter.inboxTags - For unified threads, uniqueTags are InboxTags with entityId instead of SMSTags with smsId
  if (
    listUnifiedThreadsRequest.unifiedFilter?.inboxTags?.length &&
    !listUnifiedThreadsRequest.unifiedFilter.inboxTags.some((tagId) =>
      thread.uniqueTags?.some((tag) => tag.tagId === tagId)
    )
  ) {
    return false;
  }

  // unifiedFilter.taskStatuses
  if (listUnifiedThreadsRequest.unifiedFilter?.taskStatuses?.length) {
    const threadTasks = thread.tasks || [];
    if (threadTasks.length === 0) {
      return false; // Thread has no tasks, shouldn't be visible when task status filter is applied
    }

    // Check if any of the thread's tasks match the filtered task statuses
    const hasMatchingTaskStatus = threadTasks.some((task) =>
      listUnifiedThreadsRequest.unifiedFilter?.taskStatuses?.includes(task.status)
    );

    if (!hasMatchingTaskStatus) {
      return false;
    }
  }

  // filter unifiedFilter (ai activity)
  if (listUnifiedThreadsRequest.unifiedFilter?.isActive === Bool.BOOL_TRUE) {
    const isActive = !!thread.aiStatus?.isActive;
    if (!isActive) {
      return false;
    }
  }

  if (listUnifiedThreadsRequest.unifiedFilter?.isActive === Bool.BOOL_FALSE) {
    const isActive = !!thread.aiStatus?.isActive;
    if (isActive) {
      return false;
    }
  }

  if (listUnifiedThreadsRequest.unifiedFilter?.needsAttention === Bool.BOOL_TRUE) {
    const isEscalated = !!thread.aiStatus?.isEscalated;
    if (!isEscalated) {
      return false;
    }
  }

  if (listUnifiedThreadsRequest.unifiedFilter?.aiOutcomes?.length) {
    const aiOutcomes = listUnifiedThreadsRequest.unifiedFilter?.aiOutcomes;

    if (thread.aiStatus?.status === undefined || !aiOutcomes.includes(thread.aiStatus.status)) {
      return false;
    }

    const outcomes = [
      ThreadAIOutcome.THREAD_AI_OUTCOME_SCHEDULING_COMPLETE,
      ThreadAIOutcome.THREAD_AI_OUTCOME_BOOKING_COMPLETION,
      ThreadAIOutcome.THREAD_AI_OUTCOME_RESCHEDULING_COMPLETION,
      ThreadAIOutcome.THREAD_AI_OUTCOME_ASSISTANCE_COMPLETE,
      ThreadAIOutcome.THREAD_AI_OUTCOME_APPOINTMENT_CONFIRMATION,
      ThreadAIOutcome.THREAD_AI_OUTCOME_CANCELLATION_COMPLETION,
    ];

    if (!outcomes.includes(thread.aiStatus.status)) {
      return false;
    }
  }

  return true;
};
